import { navigateTo } from '@/router/router'
import { ImageSizes } from '@/style/styleCommon'
import { createImageModel } from '@/util/ImageUrl'
import { openShareModal } from '@/detailpage/globalShareModal'
import { reportEntity, type ReportedEntityType } from '@/util/reportEntity'
import { toast, toastError } from '@/components/toast'
import { openContextMenu, type ContextMenuItem } from '@/components/contextMenu'
import type { RecommendationModel } from '@/browse/RecommendationRenderer'

export interface OpenRecommendationOverflowOptions {
    readonly data: RecommendationModel
    /**
     * Originating contextmenu event — used to anchor the menu at the
     * cursor position. Prefer this over `anchor` so the menu opens
     * exactly where the user right-clicked, matching native context
     * menus. Fall back to the card's bounding rect when the caller
     * can't pass an event (e.g. keyboard activation).
     */
    readonly event?: MouseEvent
    readonly anchor?: HTMLElement
}

/**
 * Pops a context menu for a recommendation card — Share + View
 * artist (when applicable) + View detail + Report. Anchored at the
 * cursor (or at the card's right edge when activated by keyboard);
 * mirrors GH's RecommendationOverflowMenu and a native browser
 * right-click popup.
 *
 * Full play-next / add-to-queue / add-to-playlist are still gated
 * by per-entity-type mutations the recommendation card doesn't
 * always have wired.
 */
export function openRecommendationOverflow(options: OpenRecommendationOverflowOptions): void {
    const rec = options.data
    const items: ContextMenuItem[] = []

    items.push({
        label: 'Share',
        icon: 'shareIos',
        run: () => {
            const primary = rec.image
            openShareModal({
                link: rec.link
                    ? new URL(rec.link, window.location.origin).toString()
                    : window.location.href,
                title: rec.title,
                label: rec.type.toLowerCase(),
                entityType: rec.type.toLowerCase(),
                secondary: rec.artistName ?? null,
                description: null,
                image: createImageModel(primary, { scaleToSquare: ImageSizes.LARGE }),
            })
        },
    })

    // For non-Artist cards with an artistName, offer a "View artist"
    // jump. We don't have the artist id from the recommendation row,
    // so this option only wires when the entity itself has a `node`
    // we can pull an artist out of — fall back to omitting otherwise.
    const node = rec.node as {
        contributingArtists?: {
            edges?: readonly { node?: { id?: string } | null }[] | null
        } | null
    } | null
    const firstArtistId = node?.contributingArtists?.edges?.[0]?.node?.id ?? null
    if (rec.type !== 'Artist' && firstArtistId) {
        items.push({
            label: 'View artist',
            icon: 'artist',
            run: () => navigateTo(`/artists/${firstArtistId}`),
        })
    }

    if (rec.link) {
        items.push({
            label: 'View detail',
            icon: 'play',
            run: () => {
                if (rec.link) navigateTo(rec.link)
            },
        })
    }

    // Report-content menu item — surfaces for any entity with an id
    // and a known reportable type. The mutation routes the report
    // into the moderation pipeline; resolution is out of band, we
    // just toast on submit.
    const reportableType = recommendationTypeToReportType(rec.type)
    if (rec.id && reportableType) {
        items.push({
            label: `Report ${rec.type.toLowerCase()}`,
            icon: 'cancel',
            destructive: true,
            run: () => {
                if (!rec.id) return
                reportEntity({
                    id: rec.id,
                    type: reportableType,
                    label: rec.title,
                })
                    .then(() => toast('Report submitted'))
                    .catch(() => toastError("Couldn't submit report"))
            },
        })
    }

    const { x, y } = anchorXY(options)
    openContextMenu({
        items,
        x,
        y,
        title: rec.title,
    })
}

function anchorXY(options: OpenRecommendationOverflowOptions): { x: number; y: number } {
    if (options.event) return { x: options.event.clientX, y: options.event.clientY }
    if (options.anchor) {
        const rect = options.anchor.getBoundingClientRect()
        return { x: rect.right - 8, y: rect.top + 8 }
    }
    return { x: 0, y: 0 }
}

function recommendationTypeToReportType(
    type: RecommendationModel['type']
): ReportedEntityType | null {
    switch (type) {
        case 'Album':
            return 'ALBUM'
        case 'Artist':
            return 'ARTIST'
        case 'Playlist':
            return 'PLAYLIST'
        case 'Track':
            return 'TRACK'
        case 'PodcastEpisode':
            return 'PODCAST_EPISODE'
        case 'PodcastShow':
            return 'PODCAST_SHOW'
        default:
            return null
    }
}
